import {
  CaretRight,
  ChatCircle,
  Gear,
  House,
  Kanban,
  UserCircle,
} from "@phosphor-icons/react";
import {
  Avatar,
  Box,
  DropdownMenu,
  Flex,
  IconButton,
  Link,
  Text,
} from "@radix-ui/themes";
import { Link as NavLink, useRouterState } from "@tanstack/react-router";
import { usePostHog } from "posthog-js/react";
import { useState } from "react";

import { FolderTreeView } from "./AppDrawer";
import { AvatarWithInitials } from "./AvatarWithInitials";
import { EditProfileDialog } from "./EditProfileDialog";
import EnterpriseSwitcher from "./EnterpriseSwitcher";
import { Sidebar } from "./sidebar/Sidebar";
import { SidebarContent } from "./sidebar/SidebarContent";
import { SidebarFooter } from "./sidebar/SidebarFooter";
import { SidebarGroup } from "./sidebar/SidebarGroup";
import { SidebarHeader } from "./sidebar/SidebarHeader";
import { SidebarMenu } from "./sidebar/SidebarMenu";
import { SidebarMenuButton } from "./sidebar/SidebarMenuButton";
import { SidebarMenuItem } from "./sidebar/SidebarMenuItem";
import { SidebarProvider } from "./sidebar/SidebarProvider";
import { SidebarRail } from "./sidebar/SidebarRail";
import { SidebarTrigger } from "./sidebar/SidebarTrigger";
import { useSidebar } from "./sidebar/useSidebar";

import { ReactComponent as Logo } from "../images/Logo-blue.svg";
import { ReactComponent as Wordmark } from "../images/Logotype.svg";
import { askPunttChatRoute } from "../routes/ask-puntt/chatRoute";
import { askPunttProjectsRoute } from "../routes/ask-puntt/projectsRoute";
import { askPunttRoute } from "../routes/ask-puntt/route";
import { indexRoute } from "../routes/index/route";
import { roiRoute } from "../routes/roi/route";
import { archivedUsersRoute } from "../routes/settings/routes/archivedUsersRoute";
import { integrationSettingsRoute } from "../routes/settings/routes/integrationSettingsRoute";
import { knowledgeDatabaseRoute } from "../routes/settings/routes/knowledge-database/route";
import { knowledgeSettingRoute } from "../routes/settings/routes/knowledgeSettingRoute";
import { usersRoute } from "../routes/settings/routes/usersRoute";
import { ticketsRoute } from "../routes/tickets/route";
import { useAnalytics } from "../utils/analytics";
import {
  canManageBrandGuidelines,
  canAccessKnowledgeDatabase,
  canManageUsers,
  canAccessPunttDashboard,
  isMeaningfulGigsRole,
} from "../utils/auth";
import { getNameInitials } from "../utils/constants";
import { isNil } from "../utils/fp";
import { useAppDispatch, useAppSelector } from "../utils/hooks";
import { assetForUser } from "../utils/imageHandler";
import { useAskPunttChats } from "../utils/queries/askPuntt";
import { logout } from "../utils/slices/auth";
import { setDrawerOpen } from "../utils/slices/ui";

const navLinkClasses =
  "transition-colors [&_button]:transition-colors [&_button]:hover:bg-puntt-neutral-grayA-2";
const activeNavLinkClasses =
  "[&_button]:!bg-puntt-neutral-grayA-3 [&_button]:hover:!bg-puntt-neutral-grayA-4 [&_button]:active:!bg-puntt-neutral-grayA-5";

export function AppSidebar() {
  const posthog = usePostHog();
  const analytics = useAnalytics("AppSidebar");
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);

  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.value)!;
  const sidebarOpen = useAppSelector((state) => state.ui.drawerOpen.app);

  return (
    <SidebarProvider
      name="app"
      open={sidebarOpen}
      onOpenChange={(open) => {
        analytics.capture(`sidebar_${open ? "opened" : "closed"}`);
        dispatch(setDrawerOpen({ app: open }));
      }}
    >
      <Sidebar data-testid="app-sidebar">
        <AppSidebarHeader />

        <SidebarContent data-testid="app-sidebar-content">
          <Flex direction="column" className="min-h-0 flex-1">
            <SidebarGroup className="pb-0">
              <SidebarMenu>
                <SidebarMenuItem
                  data-testid="sidebar-menu-item-roi-dashboard"
                  data-auth-trigger="roi-dashboard"
                  hidden={!isMeaningfulGigsRole()}
                >
                  <Link size="3" weight="medium" className="puntt-link" asChild>
                    <NavLink
                      to={roiRoute.to}
                      className={navLinkClasses}
                      activeProps={{
                        className: activeNavLinkClasses,
                      }}
                    >
                      <SidebarMenuButton
                        tooltip={
                          <Flex align="center" className="gap-3">
                            <House
                              size={18}
                              color="rgb(var(--puntt-blue-12))"
                            />
                            <Text
                              size="3"
                              className="puntt-link translate-y-1/2 opacity-0 duration-150 group-hover/menu-item-tooltip:translate-y-0 group-hover/menu-item-tooltip:opacity-100"
                            >
                              ROI Dashboard
                            </Text>
                          </Flex>
                        }
                      >
                        <Box className="shrink-0 p-1">
                          <House size={18} color="rgb(var(--puntt-blue-12))" />
                        </Box>
                        ROI Dashboard
                      </SidebarMenuButton>
                    </NavLink>
                  </Link>
                </SidebarMenuItem>

                <SidebarMenuItem
                  data-testid="sidebar-menu-item-dashboard"
                  data-auth-trigger="dashboard"
                  hidden={!canAccessPunttDashboard()}
                >
                  <Link size="3" weight="medium" className="puntt-link" asChild>
                    <NavLink
                      to={indexRoute.to}
                      className={navLinkClasses}
                      activeProps={{
                        className: activeNavLinkClasses,
                      }}
                    >
                      <SidebarMenuButton
                        tooltip={
                          <Flex align="center" className="gap-3">
                            <House
                              size={18}
                              color="rgb(var(--puntt-blue-12))"
                            />
                            <Text
                              size="3"
                              className="puntt-link translate-y-1/2 opacity-0 duration-150 group-hover/menu-item-tooltip:translate-y-0 group-hover/menu-item-tooltip:opacity-100"
                            >
                              Home
                            </Text>
                          </Flex>
                        }
                      >
                        <Box className="shrink-0 p-1">
                          <House size={18} color="rgb(var(--puntt-blue-12))" />
                        </Box>
                        Home
                      </SidebarMenuButton>
                    </NavLink>
                  </Link>
                </SidebarMenuItem>

                <SidebarMenuItem
                  data-testid="sidebar-menu-item-projects"
                  data-auth-trigger="projects"
                  hidden={canAccessPunttDashboard()}
                >
                  <Link size="3" weight="medium" className="puntt-link" asChild>
                    <NavLink
                      to={ticketsRoute.to}
                      className={navLinkClasses}
                      activeProps={{
                        className: activeNavLinkClasses,
                      }}
                    >
                      <SidebarMenuButton
                        tooltip={
                          <Flex align="center" className="gap-3">
                            <Kanban
                              size={18}
                              color="rgb(var(--puntt-blue-12))"
                            />
                            <Text
                              size="3"
                              className="puntt-link translate-y-1/2 opacity-0 duration-150 group-hover/menu-item-tooltip:translate-y-0 group-hover/menu-item-tooltip:opacity-100"
                            >
                              Projects
                            </Text>
                          </Flex>
                        }
                      >
                        <Box className="shrink-0 p-1">
                          <Kanban size={18} color="rgb(var(--puntt-blue-12))" />
                        </Box>
                        Projects
                      </SidebarMenuButton>
                    </NavLink>
                  </Link>
                </SidebarMenuItem>

                <SidebarMenuItem
                  data-testid="sidebar-menu-item-ask-puntt"
                  data-auth-trigger="ask-puntt"
                  hidden={!isMeaningfulGigsRole()}
                >
                  <Link size="3" weight="medium" className="puntt-link" asChild>
                    <NavLink
                      to={askPunttRoute.to}
                      className={navLinkClasses}
                      activeProps={{
                        className: activeNavLinkClasses,
                      }}
                    >
                      <SidebarMenuButton
                        tooltip={
                          <Flex align="center" className="gap-3">
                            <ChatCircle
                              size={18}
                              color="rgb(var(--puntt-blue-12))"
                            />
                            <Text
                              size="3"
                              className="puntt-link translate-y-1/2 opacity-0 duration-150 group-hover/menu-item-tooltip:translate-y-0 group-hover/menu-item-tooltip:opacity-100"
                            >
                              Ask Puntt
                            </Text>
                          </Flex>
                        }
                      >
                        <Box className="shrink-0 p-1">
                          <ChatCircle
                            size={18}
                            color="rgb(var(--puntt-blue-12))"
                          />
                        </Box>
                        Ask Puntt
                      </SidebarMenuButton>
                    </NavLink>
                  </Link>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroup>

            <AskPunttProjects />
            <AskPunttRecents />

            <FolderTreeView />
          </Flex>
        </SidebarContent>

        <SidebarFooter data-testid="app-sidebar-footer">
          <SidebarMenu>
            <SidebarMenuItem
              hidden={
                !canAccessKnowledgeDatabase() &&
                !canManageBrandGuidelines() &&
                !canManageUsers()
              }
            >
              <DropdownMenu.Root
                open={isSettingsOpen}
                onOpenChange={setIsSettingsOpen}
              >
                <DropdownMenu.Trigger
                  className="group/dropdown-menu"
                  data-testid="sidebar-settings-trigger"
                >
                  <SidebarMenuButton className="group/menu-button !p-0">
                    <IconButton
                      size="1"
                      color="gray"
                      variant="outline"
                      asChild
                      className="p-1"
                    >
                      <Gear />
                    </IconButton>

                    <Flex
                      width="100%"
                      className="rounded-md px-1.5 py-1 transition-colors group-hover/menu-button:bg-puntt-neutral-gray-3 group-data-[state=open]/dropdown-menu:bg-puntt-neutral-gray-4"
                    >
                      <Text
                        color="gray"
                        size="1"
                        weight="medium"
                        className="flex-1 text-left"
                      >
                        Settings
                      </Text>

                      <CaretRight />
                    </Flex>
                  </SidebarMenuButton>
                </DropdownMenu.Trigger>

                <DropdownMenu.Content side="right">
                  <DropdownMenu.Item
                    hidden={!canManageBrandGuidelines()}
                    data-testid="sidebar-settings-knowledge"
                  >
                    <NavLink
                      to={knowledgeSettingRoute.to}
                      className="w-full"
                      onClick={() => setIsSettingsOpen(false)}
                    >
                      Knowledge Settings
                    </NavLink>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item hidden={!canAccessKnowledgeDatabase()}>
                    <NavLink
                      to={knowledgeDatabaseRoute.to}
                      search={{ pageSize: 100 }}
                      className="w-full"
                      onClick={() => setIsSettingsOpen(false)}
                    >
                      Knowledge Database
                    </NavLink>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    hidden={!canManageUsers()}
                    data-testid="sidebar-settings-integration"
                  >
                    <NavLink
                      to={integrationSettingsRoute.to}
                      className="w-full"
                      onClick={() => setIsSettingsOpen(false)}
                    >
                      Integration Settings
                    </NavLink>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    hidden={!canManageUsers()}
                    data-testid="sidebar-settings-manage-users"
                  >
                    <NavLink
                      to={usersRoute.to}
                      className="w-full"
                      onClick={() => setIsSettingsOpen(false)}
                    >
                      Manage Users
                    </NavLink>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item hidden={!canManageUsers()}>
                    <NavLink
                      to={archivedUsersRoute.to}
                      className="w-full"
                      onClick={() => setIsSettingsOpen(false)}
                    >
                      Archived Users
                    </NavLink>
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </SidebarMenuItem>

            <SidebarMenuItem
              hidden={!isNil(user.email)}
              data-auth-trigger="login-signup-button"
            >
              <SidebarMenuButton className="group/menu-button !p-0">
                <IconButton
                  size="1"
                  color="blue"
                  variant="outline"
                  asChild
                  className="p-1"
                >
                  <UserCircle />
                </IconButton>

                <Flex
                  width="100%"
                  className="rounded-md px-1.5 py-1 transition-colors group-hover/menu-button:bg-puntt-neutral-gray-3"
                >
                  <Text
                    color="blue"
                    size="1"
                    weight="medium"
                    className="flex-1 whitespace-nowrap text-left"
                  >
                    Log In or Sign Up
                  </Text>
                </Flex>
              </SidebarMenuButton>
            </SidebarMenuItem>

            <SidebarMenuItem hidden={isNil(user.email)}>
              <DropdownMenu.Root
                open={isAccountOpen}
                onOpenChange={setIsAccountOpen}
              >
                <DropdownMenu.Trigger
                  className="group/dropdown-menu"
                  data-testid="sidebar-account-trigger"
                >
                  <SidebarMenuButton className="group/menu-button !p-0">
                    <Avatar
                      variant="solid"
                      size="1"
                      src={assetForUser(user.avatar)}
                      fallback={getNameInitials(user.name)}
                    />

                    <Flex
                      width="100%"
                      className="rounded-md px-1.5 py-1 transition-colors group-hover/menu-button:bg-puntt-neutral-gray-3 group-data-[state=open]/dropdown-menu:bg-puntt-neutral-gray-4"
                    >
                      <Text
                        color="gray"
                        size="1"
                        weight="medium"
                        className="flex-1 text-left"
                      >
                        Account
                      </Text>

                      <CaretRight />
                    </Flex>
                  </SidebarMenuButton>
                </DropdownMenu.Trigger>

                <DropdownMenu.Content side="right" className="min-w-[220px]">
                  {/* Header area */}
                  <div className="flex flex-col items-center py-4">
                    <AvatarWithInitials
                      size={16}
                      name={user.name}
                      avatar={user.avatar}
                    />
                    <span className="mt-2 text-center text-lg font-semibold">
                      {user.name}
                    </span>
                    <span className="text-gray-500 mt-1 text-center text-sm">
                      {user.email}
                    </span>
                  </div>
                  <div className="border-gray-200 my-2 border-t" />
                  <DropdownMenu.Item
                    onClick={() => {
                      setIsEditProfileOpen(true);
                      setIsAccountOpen(false);
                    }}
                    className="cursor-pointer"
                  >
                    Edit Profile
                  </DropdownMenu.Item>
                  <div className="border-gray-200 my-2 border-t" />
                  <DropdownMenu.Item
                    onClick={() => {
                      dispatch(logout());
                      posthog.reset();
                      setIsAccountOpen(false);
                    }}
                    className="text-red-600 cursor-pointer"
                    data-testid="sidebar-account-logout"
                  >
                    Log Out
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
        <SidebarRail data-testid="app-sidebar-rail" />
        <SidebarTrigger data-testid="app-sidebar-trigger" />
      </Sidebar>

      <EditProfileDialog
        open={isEditProfileOpen}
        onOpenChange={setIsEditProfileOpen}
      />
    </SidebarProvider>
  );
}

function AppSidebarHeader() {
  const user = useAppSelector((state) => state.auth.value);
  const { open } = useSidebar();

  return (
    <SidebarHeader
      data-testid="app-sidebar-header"
      data-auth-trigger="sidebar-header"
    >
      <Flex gap="4" justify={open ? "between" : "center"} align="center">
        <NavLink to={indexRoute.to}>
          {open ? (
            <Wordmark className="h-4 max-w-max" />
          ) : (
            <Logo className="size-6" />
          )}
        </NavLink>

        {open && !isNil(user?.email) ? <EnterpriseSwitcher /> : null}
      </Flex>
    </SidebarHeader>
  );
}

function projectConfidenceColor(confidence: number | undefined): string {
  if (confidence == null) return "bg-puntt-neutral-gray-8";
  if (confidence >= 80) return "bg-malachite-500";
  if (confidence >= 60) return "bg-puntt-gold-9";
  return "bg-puntt-red-9";
}

function AskPunttProjects() {
  const { data } = useAskPunttChats();
  const { open } = useSidebar();
  const { matches } = useRouterState();
  const isOnAskPuntt = matches.some((m) => m.pathname.includes("/ask-puntt"));

  if (!isMeaningfulGigsRole() || !open || !isOnAskPuntt) return null;

  const projects =
    data?.chats?.filter((c) => c.workflowType === "project" && c.projectData) ??
    [];
  if (projects.length === 0) return null;

  return (
    <SidebarGroup>
      <Flex justify="between" align="center" className="px-3 pb-1">
        <Text
          size="1"
          weight="bold"
          className="uppercase tracking-wider text-puntt-neutral-gray-9"
        >
          Projects
        </Text>
        <Link size="1" className="puntt-link" asChild>
          <NavLink
            to={askPunttProjectsRoute.to}
            className="text-puntt-neutral-gray-9 transition-colors hover:text-puntt-accent-9"
          >
            <Text size="1">All projects →</Text>
          </NavLink>
        </Link>
      </Flex>
      <SidebarMenu>
        {projects.slice(0, 8).map((chat) => (
          <SidebarMenuItem key={chat._id}>
            <Link size="2" className="puntt-link" asChild>
              <NavLink
                to={askPunttChatRoute.to}
                params={{ chatId: chat._id }}
                className={navLinkClasses}
                activeProps={{
                  className: activeNavLinkClasses,
                }}
              >
                <SidebarMenuButton className="pl-3">
                  <span className="flex items-center gap-2">
                    <span
                      className={`size-2 shrink-0 rounded-full ${projectConfidenceColor(
                        chat.projectData?.forecast?.confidence,
                      )}`}
                    />
                    <Text
                      size="1"
                      className="truncate text-puntt-neutral-gray-11"
                    >
                      {chat.projectData?.name || chat.title}
                    </Text>
                  </span>
                </SidebarMenuButton>
              </NavLink>
            </Link>
          </SidebarMenuItem>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}

function AskPunttRecents() {
  const { data } = useAskPunttChats();
  const { open } = useSidebar();
  const { matches } = useRouterState();
  const isOnAskPuntt = matches.some((m) => m.pathname.includes("/ask-puntt"));

  if (!isMeaningfulGigsRole() || !open || !isOnAskPuntt || !data?.chats?.length)
    return null;

  const nonProjectChats =
    data.chats.filter((c) => c.workflowType !== "project") ?? [];
  if (nonProjectChats.length === 0) return null;

  return (
    <SidebarGroup>
      <Text
        size="1"
        weight="bold"
        className="px-3 pb-1 uppercase tracking-wider text-puntt-neutral-gray-9"
      >
        Chats
      </Text>
      <SidebarMenu>
        {nonProjectChats.slice(0, 8).map((chat) => (
          <SidebarMenuItem key={chat._id}>
            <Link size="2" className="puntt-link" asChild>
              <NavLink
                to={askPunttChatRoute.to}
                params={{ chatId: chat._id }}
                className={navLinkClasses}
                activeProps={{
                  className: activeNavLinkClasses,
                }}
              >
                <SidebarMenuButton className="pl-3">
                  <Text
                    size="1"
                    className="truncate text-puntt-neutral-gray-11"
                  >
                    {chat.title}
                  </Text>
                </SidebarMenuButton>
              </NavLink>
            </Link>
          </SidebarMenuItem>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}
